/* Right-click a square: everything that square can be told.
 *
 * This used to be a short list — was it asked for, and the old jump to the
 * appointment picker — with everything else a left-click away in a modal sheet
 * over the middle of the screen. The sheet is gone from the board. A square is a
 * thing at a place, the desk clicked that place to get here, and a dialog in the
 * centre of the window made them look away from the column they were working to
 * read back facts about it. So the sheet's buttons came into the menu that was
 * already anchored under the pointer.
 *
 * What that costs is height: this card is taller than the skip menu and can grow
 * again when the time fields or the service list open under it. `pos` clamps the
 * top and hands the card whatever is left below it as a max height, so an
 * expansion near the bottom of the screen scrolls rather than running off it.
 *
 * The sheet itself still exists — js/components/cell-menu.js — because the phone
 * list and the appointments pane have no right-click to offer.
 */
import * as Vue from 'vue';
import { store } from '../store.js';
import { useMenuFocus } from '../menu-focus.js';

var MENU_W = 296;
var MENU_H = 420;   // enough to keep the resting card on screen near an edge
var EDGE = 8;

export const TurnMenu = {
  name: 'TurnMenu',
  setup: function () {
    var turn = store.turnMenuTurn;
    var focus = useMenuFocus(Vue.computed(function () { return !!turn.value; }));
    var confirmRemove = Vue.ref(false);
    var changing = Vue.ref(false);
    var editingTimes = Vue.ref(false);
    var startInput = Vue.ref('');
    var endInput = Vue.ref('');

    // Closing nulls the target, so every reopen — including the hop to the
    // other half of a shared square — starts with nothing half-done showing.
    Vue.watch(turn, function () {
      confirmRemove.value = false;
      changing.value = false;
      editingTimes.value = false;
    });

    var pos = Vue.computed(function () {
      var m = store.ui.turnMenu;
      if (!m) return { left: '0px', top: '0px' };
      var w = window.innerWidth, h = window.innerHeight;
      var top = Math.max(EDGE, Math.min(m.y, h - MENU_H - EDGE));
      return {
        left: Math.max(EDGE, Math.min(m.x, w - MENU_W - EDGE)) + 'px',
        top: top + 'px',
        maxHeight: Math.max(200, h - top - EDGE) + 'px'
      };
    });

    var staff = Vue.computed(function () {
      return turn.value ? store.staffOf(turn.value.staffId) : null;
    });

    var name = Vue.computed(function () {
      return (staff.value && staff.value.name) || (turn.value && turn.value.staffName) || '';
    });

    var turnNumber = Vue.computed(function () {
      return turn.value ? store.turnRowNumber(turn.value.id) : 0;
    });

    var color = Vue.computed(function () {
      return store.colorOfTurn(turn.value);
    });

    var elapsed = Vue.computed(function () {
      if (!turn.value) return '0:00';
      var end = turn.value.endedAt == null ? store.now.value : turn.value.endedAt;
      return store.formatDuration(end - turn.value.startedAt);
    });

    /* When the booking behind the square was for, and how far from it the
     * square started — said whatever the size of the difference, because this
     * is where the desk comes to check it. Coloured only past the salon's
     * margin, the same test that marks the bar; see js/rules/start-offset.js. */
    var offset = Vue.computed(function () {
      var t = turn.value;
      var o = t && store.startOffsetOf(t);
      if (!o) return null;
      var mins = Math.round(Math.abs(o.diff) / 60000);
      var text = mins === 0 ? 'on time'
        : store.formatMins(Math.abs(o.diff)) + (o.diff > 0 ? ' late' : ' early');
      return {
        booked: store.formatClock(o.bookedAt),
        text: text,
        kind: o.flag ? o.flag.kind : null
      };
    });

    // The other half sharing this square, so both are reachable from here.
    var mates = Vue.computed(function () {
      if (!turn.value) return [];
      var list = store.slotMatesOf(turn.value.id);
      return list.length > 1 ? list : [];
    });

    // Everything on the booking, not only the options carrying a rule: the
    // square's + counts them all, and a menu that listed fewer than the mark
    // promised would read as a bug.
    var options = Vue.computed(function () {
      return turn.value ? store.optionsOfTurn(turn.value) : [];
    });

    // Which of them is the reason this square is the type it is. The rest are
    // notes on the booking, and the menu says which is which rather than
    // leaving the desk to guess why a pedicure counts as a full turn.
    function optionRule(o) {
      var r = store.optionRuleFor(o.id);
      if (!r || !r.serviceId) return null;
      return store.serviceById.value[r.serviceId] || null;
    }

    // The three weights read as three different sentences — "worth no turn"
    // is not English, and a zero-turn service is worth saying out loud.
    var WORTH = {
      0: ', which does not count as a turn at all',
      0.5: ', worth half a turn',
      1: ', worth a full turn'
    };

    function optionTitle(o) {
      var svc = optionRule(o);
      if (!svc) return o.name + ' — a note on the booking; it does not change the turn';
      var article = /^[aeiou]/i.test(svc.name) ? 'an ' : 'a ';
      return o.name + ' — makes this ' + article + svc.name +
        (WORTH[svc.turnWeight] || '');
    }

    /* ---- the star ---- */

    var requestsOn = Vue.computed(function () { return !!store.ui.requestsOn; });

    // Not marked here while a booking system is connected (Kevin, 2026-10-25)
    // — see `store.deskMarksRequests`.
    var deskMarks = store.deskMarksRequests;

    var marked = Vue.computed(function () {
      return !!(turn.value && turn.value.requested);
    });

    // Whole sentences rather than text stitched around {{ }}: the compiler
    // eats the whitespace either side of an interpolation and the seams show.
    var label = Vue.computed(function () {
      if (!name.value) return marked.value ? 'Not a client request' : 'Mark as a client request';
      return marked.value ? 'Not a request for ' + name.value : 'Client asked for ' + name.value;
    });

    // The booking behind the square, if any — and whether the book agrees who
    // is doing it. Those two decide whether the mark travels to the booking or
    // stays on the square, so the menu says which it will be.
    var linked = Vue.computed(function () {
      var t = turn.value;
      return !!(t && t.appointmentServiceId && store.bookedServiceFor(t));
    });

    var mismatched = Vue.computed(function () {
      var t = turn.value;
      if (!linked.value) return false;
      var found = store.bookedServiceFor(t);
      var booked = found && store.bookedStaffFor(found.svc);
      return !!(booked && booked.id !== t.staffId);
    });

    var hint = Vue.computed(function () {
      if (!turn.value) return '';
      if (mismatched.value) {
        return 'The book has this booking with somebody else, so the star ' +
          'stays on the square rather than going onto the appointment.';
      }
      if (linked.value) {
        return marked.value
          ? 'Marked on the appointment as well, so the book and the grid agree.'
          : 'Marks the appointment too, so it still reads as a request in ' +
            'today’s book.';
      }
      return 'A walk-in, so the star lives on this square.';
    });

    var canAttach = Vue.computed(function () {
      return !!(store.schedulingOn.value && turn.value && !turn.value.appointmentServiceId);
    });

    /* ---- changing what the square is ----
     *
     * Changing a turn's service must not put a tech on something they don't
     * do. The service it already is stays enabled whatever their list says —
     * it can be booked off-menu deliberately, and greying out the current
     * selection would just look broken.
     */
    function canDo(svc) {
      if (!staff.value) return true;
      if (turn.value && svc.id === turn.value.serviceId) return true;
      return staff.value.serviceIds.indexOf(svc.id) !== -1;
    }

    var offMenuCount = Vue.computed(function () {
      return store.activeServices.value.filter(function (s) { return !canDo(s); }).length;
    });

    function pickService(svc) {
      if (!turn.value) return;
      store.setTurnService(turn.value.id, svc.id);
      changing.value = false;
    }

    /* ---- the clock ----
     *
     * Seeded from the square each time, so cancelling and reopening the editor
     * never shows a stale value somebody half-typed earlier.
     */
    function editTimes() {
      if (!turn.value) return;
      startInput.value = store.clockValue(turn.value.startedAt);
      endInput.value = store.clockValue(turn.value.endedAt);
      editingTimes.value = true;
    }

    function saveTimes() {
      if (!turn.value) return;
      if (store.setTurnTimes(turn.value.id, startInput.value, endInput.value)) {
        editingTimes.value = false;
      }
    }

    function toggleTimes() {
      if (editingTimes.value) editingTimes.value = false;
      else editTimes();
    }

    /* ---- the acts that end the menu ----
     *
     * Stopping, reopening and removing all answer the question the menu was
     * opened to ask, so the card goes with them. Changing the service and
     * fixing the clock do not: those are corrections, and the desk usually
     * wants to see the square agree before letting go of it.
     */
    function stop() {
      if (turn.value) store.stopTurn(turn.value.id);
    }

    function reopen() {
      if (turn.value) store.reopenTurn(turn.value.id);
    }

    function remove() {
      if (turn.value) store.removeTurn(turn.value.id);
    }

    function toggle() {
      if (!turn.value) return;
      store.toggleTurnRequest(turn.value.id);
      store.closeTurnMenu();
    }

    function attach() {
      if (!turn.value) return;
      var id = turn.value.id;
      store.closeTurnMenu();
      store.openAttachPick(id);
    }

    // The other half, in the place the pointer already is — the card stays put
    // and changes what it is about, rather than jumping to a second gesture.
    function showMate(id) {
      var m = store.ui.turnMenu;
      if (m) store.openTurnMenu(id, m.x, m.y);
    }

    return {
      turn: turn, pos: pos, staff: staff, name: name, turnNumber: turnNumber,
      color: color, elapsed: elapsed, mates: mates, offset: offset,
      options: options, optionRule: optionRule, optionTitle: optionTitle,
      requestsOn: requestsOn, deskMarks: deskMarks, marked: marked, label: label, hint: hint,
      canAttach: canAttach,
      confirmRemove: confirmRemove,
      changing: changing, canDo: canDo, offMenuCount: offMenuCount,
      pickService: pickService, services: store.activeServices,
      editingTimes: editingTimes, startInput: startInput, endInput: endInput,
      editTimes: editTimes, saveTimes: saveTimes, toggleTimes: toggleTimes,
      stop: stop, reopen: reopen, remove: remove,
      toggle: toggle, attach: attach, showMate: showMate,
      colorOfTurn: store.colorOfTurn,
      formatClock: store.formatClock,
      formatCredit: store.formatCredit,
      menuEl: focus.menuEl,
      onMenuKey: focus.onMenuKey,
      close: store.closeTurnMenu
    };
  },
  template: [
    '<div v-if="turn" class="ctx-scrim" @click.self="close" @contextmenu.prevent="close">',
    '  <div class="ctx ctx--turn" :style="pos" role="menu"',
    '       ref="menuEl" @keydown="onMenuKey">',
    /* The square's own colour and letter on the card, the way the sheet carried
       them: the menu opens over the grid and has to say which square it took. */
    '    <div class="ctx__head ctx__head--turn" :style="{ \'--svc\': color }">',
    '      <span class="ctx__letter" aria-hidden="true">{{ turn.serviceLetter }}</span>',
    '      <span class="ctx__head-text">',
    '        <strong>{{ name }}</strong>',
    '        <span>{{ turn.serviceName }} &middot; Turn {{ turnNumber }}</span>',
    '      </span>',
    '      <span v-if="requestsOn && turn.requested" class="ctx__tag">&#9733;</span>',
    '    </div>',
    '',
    '    <dl class="ctx__facts">',
    '      <div v-if="offset" class="ctx__facts-wide"><dt>Booked</dt><dd>{{ offset.booked }}',
    '        <span class="ctx__offset" :class="offset.kind ? \'is-\' + offset.kind : \'\'">',
    '          &middot; started {{ offset.text }}</span></dd></div>',
    '      <div><dt>Started</dt><dd>{{ formatClock(turn.startedAt) }}</dd></div>',
    '      <div><dt>Ended</dt>',
    '        <dd>{{ turn.endedAt ? formatClock(turn.endedAt) : \'In progress\' }}</dd></div>',
    '      <div><dt>Elapsed</dt><dd class="ctx__elapsed">{{ elapsed }}</dd></div>',
    '      <div><dt>Counts as</dt><dd>{{ formatCredit(turn.turnWeight) }}</dd></div>',
    '    </dl>',
    '',
    /* Part of what the service in the head IS. The one that set the type is
       filled in; the rest are notes on the booking. */
    '    <template v-if="options.length">',
    '      <p class="ctx__label">{{ options.length === 1 ? "Option" : "Options" }}</p>',
    '      <div class="ctx__pills">',
    '        <span v-for="o in options" :key="o.id" class="opt-pill"',
    '          :class="{ \'opt-pill--rules\': optionRule(o) }" :title="optionTitle(o)">',
    '          {{ o.name }}</span>',
    '      </div>',
    '    </template>',
    '',
    '    <template v-if="mates.length">',
    '      <p class="ctx__label">Half turns sharing this square</p>',
    '      <div class="ctx__chips">',
    '        <button v-for="m in mates" :key="m.id" type="button" class="chip chip--sm"',
    '          :class="{ \'chip--on\': m.id === turn.id }"',
    '          :style="{ \'--chip\': colorOfTurn(m) }"',
    '          @click="showMate(m.id)">',
    '          <span class="chip__letter">{{ m.serviceLetter }}</span>',
    '          <span class="chip__name">{{ m.serviceName }}</span>',
    '        </button>',
    '      </div>',
    '    </template>',
    '',
    '    <p class="ctx__label">This square</p>',
    /* First, and the only item on the card that is what the desk came for nine
       times in ten. */
    '    <button v-if="turn.endedAt == null" type="button" class="ctx__item ctx__item--stop"',
    '      role="menuitem" @click="stop">',
    '      <span class="ctx__box ctx__box--stop" aria-hidden="true">&#9632;</span>',
    '      <span class="ctx__label-text">Stop service</span>',
    '    </button>',
    '    <button v-else type="button" class="ctx__item ctx__item--back" role="menuitem"',
    '      @click="reopen">',
    '      <span class="ctx__box" aria-hidden="true"></span>',
    '      <span class="ctx__label-text">Reopen service</span>',
    '    </button>',
    '',
    '    <button v-if="requestsOn && deskMarks" type="button" class="ctx__item" role="menuitemcheckbox"',
    '      :class="{ \'is-on\': marked }" :aria-checked="marked ? \'true\' : \'false\'"',
    '      @click="toggle">',
    '      <span class="ctx__box ctx__box--star" aria-hidden="true">{{ marked ? "\\u2605" : "\\u2606" }}</span>',
    '      <span class="ctx__label-text">{{ label }}</span>',
    '    </button>',
    '',
    /* The shortcut right-click used to be on its own. Kept, so nobody loses a
       gesture they had; hidden once the square already has a booking. */
    '    <button v-if="canAttach" type="button" class="ctx__item" role="menuitem" @click="attach">',
    '      <span class="ctx__box" aria-hidden="true"></span>',
    '      <span class="ctx__label-text">Attach an appointment&hellip;</span>',
    '    </button>',
    '',
    '    <button type="button" class="ctx__item" role="menuitem"',
    '      @click="toggleTimes">',
    '      <span class="ctx__box" aria-hidden="true"></span>',
    '      <span class="ctx__label-text">{{ editingTimes ? "Cancel time edit" : "Edit times" }}</span>',
    '    </button>',
    '    <template v-if="editingTimes">',
    '      <div class="ctx__hours">',
    '        <label class="ctx__hours-field">',
    '          <span class="ctx__hours-key">Started</span>',
    '          <input class="input input--clock" type="time" v-model="startInput"',
    '            aria-label="Time this service started" />',
    '        </label>',
    '        <span class="ctx__hours-dash">&ndash;</span>',
    '        <label class="ctx__hours-field">',
    '          <span class="ctx__hours-key">Ended</span>',
    '          <input class="input input--clock" type="time" v-model="endInput"',
    '            aria-label="Time this service finished" />',
    '        </label>',
    '      </div>',
    '      <p class="ctx__hint">Both times are on {{ turn.businessDate }}. Clear the finish to put this service back in progress. The square keeps its place in the column.</p>',
    '      <p class="ctx__act">',
    '        <button type="button" class="btn btn--primary btn--sm" @click="saveTimes">Save times</button></p>',
    '    </template>',
    '',
    '    <button type="button" class="ctx__item" role="menuitem" @click="changing = !changing">',
    '      <span class="ctx__box" aria-hidden="true"></span>',
    '      <span class="ctx__label-text">{{ changing ? "Keep current service" : "Change service" }}</span>',
    '    </button>',
    '    <template v-if="changing">',
    '      <div class="ctx__chips">',
    '        <button v-for="svc in services" :key="svc.id" type="button" class="chip chip--sm"',
    '          :class="{ \'chip--on\': svc.id === turn.serviceId }" :style="{ \'--chip\': svc.color }"',
    '          :disabled="!canDo(svc)"',
    '          :title="canDo(svc) ? svc.name : (staff ? staff.name + \' is not set up for \' + svc.name : svc.name)"',
    '          @click="pickService(svc)">',
    '          <span class="chip__letter">{{ svc.abbrev }}</span>',
    '          <span class="chip__name">{{ svc.name }}</span>',
    '        </button>',
    '      </div>',
    '      <p v-if="offMenuCount" class="ctx__hint">',
    '        {{ offMenuCount }} greyed out &mdash; not on',
    '        {{ staff ? staff.name + "&rsquo;s" : "their" }} service list.',
    '        Add {{ offMenuCount === 1 ? "it" : "them" }} under <strong>Settings &rarr; Staff</strong>.</p>',
    '    </template>',
    '',
    '    <button v-if="!confirmRemove" type="button" class="ctx__item ctx__item--out"',
    '      role="menuitem" @click="confirmRemove = true">',
    '      <span class="ctx__box" aria-hidden="true">&times;</span>',
    '      <span class="ctx__label-text">Remove turn</span>',
    '    </button>',
    '    <button v-else type="button" class="ctx__item ctx__item--out ctx__item--confirm"',
    '      role="menuitem" @click="remove">',
    '      <span class="ctx__box" aria-hidden="true">&times;</span>',
    '      <span class="ctx__label-text">Really remove &mdash; later turns shift up</span>',
    '    </button>',
    '',
    '    <p v-if="requestsOn" class="ctx__hint">{{ hint }}</p>',
    '  </div>',
    '</div>'
  ].join('\n')
};
